import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { CircularProgress, LinearProgress } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import { Box, Card, CardContent, CardHeader, Typography } from '@mui/material';
import { Button, Chip, IconButton, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SpeedIcon from '@mui/icons-material/Speed';
import logger from '../../utils/logger';

interface TransactionStatusProps {
    status: string;
    txHash?: string;
    error?: string;
    provider?: ethers.providers.Web3Provider;
    onRetry?: () => void;
}

const TransactionStatus: React.FC<TransactionStatusProps> = ({
    status,
    txHash,
    error,
    provider,
    onRetry
}) => {
    const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
    const [gasPrice, setGasPrice] = useState<string | null>(null);
    const [networkCongestion, setNetworkCongestion] = useState<'low' | 'medium' | 'high' | null>(null);
    const [confirmations, setConfirmations] = useState<number>(0);
    const [progress, setProgress] = useState(0);

    // Determine the status type
    const isPending = status.includes('Estimated') || status.includes('initiated') || status.includes('Collecting');
    const isSuccess = status.includes('executed') || status.includes('Transaction Hash') || status.includes('Completed');
    const isError = status.includes('Error') || status.includes('failed') || !!error;
    const isNeutral = !isPending && !isSuccess && !isError;

    useEffect(() => {
        let intervalId: NodeJS.Timeout;

        const fetchTransactionStatus = async () => {
            if (!provider || !txHash || !isPending) return;

            try {
                // Get current gas price
                const currentGasPrice = await provider.getGasPrice();
                setGasPrice(ethers.utils.formatUnits(currentGasPrice, 'gwei'));

                // Get transaction
                const tx = await provider.getTransaction(txHash);
                if (!tx) return;

                // Get latest block
                const currentBlock = await provider.getBlockNumber();

                if (tx.blockNumber) {
                    const newConfirmations = currentBlock - tx.blockNumber + 1;
                    setConfirmations(newConfirmations);
                    setProgress(Math.min((newConfirmations / 12) * 100, 100)); // 12 confirmations for full progress
                }

                // Estimate remaining time based on gas price
                const baseGasPrice = ethers.utils.parseUnits('30', 'gwei');
                const congestionRatio = currentGasPrice.div(baseGasPrice);

                if (congestionRatio.lt(2)) {
                    setNetworkCongestion('low');
                    setTimeRemaining(15);
                } else if (congestionRatio.lt(4)) {
                    setNetworkCongestion('medium');
                    setTimeRemaining(30);
                } else {
                    setNetworkCongestion('high');
                    setTimeRemaining(60);
                }
            } catch (error) {
                logger.error('Error fetching transaction status:', error);
            }
        };

        if (isPending && provider && txHash) {
            fetchTransactionStatus();
            intervalId = setInterval(fetchTransactionStatus, 3000);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [provider, txHash, isPending]);

    const getCongestionColor = (level: 'low' | 'medium' | 'high') => {
        switch (level) {
            case 'low': return 'success';
            case 'medium': return 'warning';
            case 'high': return 'error';
            default: return 'default';
        }
    };

    const formatTimeRemaining = (seconds: number) => {
        if (seconds < 60) return `~${seconds} seconds`;
        return `~${Math.ceil(seconds / 60)} minutes`;
    };

    return (
        <Card sx={{ mt: 3 }}>
            <CardHeader
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isPending && <PendingIcon color="warning" />}
                            {isSuccess && <CheckCircleIcon color="success" />}
                            {isError && <ErrorIcon color="error" />}
                            <Typography variant="h6">Transaction Status</Typography>
                        </Box>
                        <Chip
                            label={isPending ? "Pending" : isSuccess ? "Success" : isNeutral ? "Neutral" : "Error"}
                            color={isPending ? "warning" : isSuccess ? "success" : isNeutral ? "default" : "error"}
                            variant="outlined"
                        />
                    </Box>
                }
            />

            <CardContent>
                {/* Main Status Message */}
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {status}
                </Typography>

                {/* Progress Section for Pending Transactions */}
                {isPending && (
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Progress</Typography>
                            <Typography variant="body2">{confirmations} confirmations</Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ mb: 1 }}
                        />
                        {timeRemaining && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTimeIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="textSecondary">
                                    {formatTimeRemaining(timeRemaining)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Network Status */}
                {gasPrice && networkCongestion && (
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <NetworkCheckIcon fontSize="small" />
                            <Typography variant="body2">Network Status</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Current gas price">
                                <Chip
                                    icon={<SpeedIcon />}
                                    label={`${Number(gasPrice).toFixed(1)} Gwei`}
                                    size="small"
                                    variant="outlined"
                                />
                            </Tooltip>
                            <Tooltip title="Network congestion level">
                                <Chip
                                    label={`${networkCongestion.charAt(0).toUpperCase() + networkCongestion.slice(1)} Congestion`}
                                    size="small"
                                    color={getCongestionColor(networkCongestion)}
                                    variant="outlined"
                                />
                            </Tooltip>
                        </Box>
                    </Box>
                )}

                {/* Transaction Hash */}
                {txHash && (
                    <Box sx={{
                        bgcolor: 'action.hover',
                        p: 2,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        wordBreak: 'break-all'
                    }}>
                        Transaction Hash: {txHash}
                    </Box>
                )}

                {/* Error Message and Retry Button */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mt: 2 }}
                        action={
                            onRetry && (
                                <Button
                                    color="error"
                                    size="small"
                                    startIcon={<RefreshIcon />}
                                    onClick={onRetry}
                                >
                                    Retry
                                </Button>
                            )
                        }
                    >
                        <AlertTitle>Error</AlertTitle>
                        {error}
                    </Alert>
                )}

                {/* Loading Indicator for Pending State */}
                {isPending && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: 2
                    }}>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <Typography variant="body2" color="textSecondary">
                            Transaction in progress...
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default TransactionStatus;